import { faBuilding, faEnvelope } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowAltCircleDown,
  faCheck,
  faMobileAlt,
  faPen,
  faPhoneAlt,
  faPlus,
  faSearch,
  faTag,
  faTimes,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList'; // Importe o EmptyList
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import ContactCustomerEditModal from 'components/modals/ContactCustomerEditModal';
import ContactImportModal from 'components/modals/ContactImportModal';
import ContactRegistrationModal from 'components/modals/ContactRegistrationModal';
import Pagination from 'components/pagination';
import { useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, FormControl, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import ContactsService from 'services/Contacts.service';

const ContactRegistration = () => {
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [showImportModal, setShowImportModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [successEdit, setSuccessEdit] = useState(false);

  const [selectedContact, setSelectedContact] = useState({});
  const [filterName, setFilterName] = useState('');
  const [contacts, setContacts] = useState({
    content: [],
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    name: '',
    includeInactives: false
  });

  const getData = async () => {
    setIsLoading(true);
    const queryParams = new URLSearchParams(filters).toString();
    const response = await ContactsService.list(queryParams);
    if (response.content) setContacts(response);
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, [filters]);

  useEffect(() => {
    setSuccessEdit(false);
  }, [selectedContact]);

  useEffect(() => {
    debouncedSearch(filterName);
  }, [filterName]);

  const columns = [
    {
      accessorKey: 'name',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900 w-25' }
      },
      cell: rowData => {
        const { name } = rowData.row.original;
        return (
          <div className="w-100">
            <Flex alignItems="center">
              <FontAwesomeIcon
                icon={faUserCircle}
                className="me-1 text-primary"
                size="lg"
              />
              <span>{name}</span>
            </Flex>
          </div>
        );
      }
    },
    {
      accessorKey: 'contact',
      header: 'Contato',
      meta: {
        headerProps: { className: 'text-900 w-50' }
      },
      cell: rowData => {
        const { email, landline, phone } = rowData.row.original;
        return (
          <Flex direction="column">
            {email && (
              <div>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="me-1"
                  style={{ width: '10px' }}
                />
                <span>{email}</span>
              </div>
            )}
            {phone && (
              <div>
                <FontAwesomeIcon
                  icon={faMobileAlt}
                  className="me-1"
                  style={{ width: '10px' }}
                />
                <span>{phone}</span>
              </div>
            )}
            {landline && (
              <div>
                <FontAwesomeIcon
                  icon={faPhoneAlt}
                  className="me-1"
                  style={{ width: '10px' }}
                />
                <span>{landline}</span>
              </div>
            )}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'tags',
      header: 'Tags',
      meta: {
        headerProps: { className: 'text-900 w-25' }
      },
      cell: rowData => {
        const { tags } = rowData.row.original;
        return (
          <>
            <Flex
              alignItems="center"
              className="fs-11 text-center"
              style={{ maxWidth: '200px' }}
              wrap="wrap"
            >
              <FontAwesomeIcon icon={faTag} className="me-2" />
              {tags && tags.length > 0
                ? tags.map(tag => (
                    <Badge
                      bg="light"
                      text="dark"
                      className="text-center me-1 mb-1 p-2"
                      style={{ fontSize: '12px' }}
                      key={tag.id}
                    >
                      {tag.name}
                    </Badge>
                  ))
                : '-'}
            </Flex>
          </>
        );
      }
    },
    {
      accessorKey: 'customers',
      header: 'Clientes',
      meta: {
        headerProps: { className: 'text-900 w-50' }
      },
      cell: rowData => {
        const { customers } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faBuilding} className="me-2" />
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '220px'
              }}
              title={customers
                .map(customer => customer.fantasyName || customer.name)
                .join(', ')}
            >
              {customers.map((customer, index) => (
                <span key={index}>
                  {customer.fantasyName || customer.name}
                  {index !== customers.length - 1 && ', '}
                </span>
              ))}
            </div>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'isPortalUser',
      header: 'Portal do Cliente',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { isPortalUser } = rowData.row.original;
        return (
          <Flex alignItems="center">
            {isPortalUser ? (
              <FontAwesomeIcon icon={faCheck} className="text-success" />
            ) : (
              <FontAwesomeIcon icon={faTimes} className="text-danger" />
            )}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'inactive',
      header: 'Ativo',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return inactive ? (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        ) : (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <button
            className="btn btn-link btn-sm"
            onClick={() => handleEdit(item)}
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </button>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: contacts.content,
    columns
  });

  const handleCloseModalRegister = () => {
    setShowModal(false);
  };

  const handleCloseModalEdit = () => {
    setShowModalEdit(false);
  };

  const handleEdit = item => {
    setSelectedContact(item);
    setShowModalEdit(true);
  };

  const editContact = async bodyChange => {
    const response = await ContactsService.edit(selectedContact.id, bodyChange);
    if (response.id) {
      showSuccessNotification('Contato editado com sucesso');
      getData();
      handleCloseModalEdit();
      setSuccessEdit(true);
    } else {
      showErrorNotification(response.userMessage);
    }
  };

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    setFilterName(searchValue);
  };

  const debouncedSearch = useDebounce(searchValue => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      name: searchValue
    }));
  }, 400);

  const isEmpty = !isLoading && contacts.content.length === 0;

  return (
    <>
      <ContactImportModal
        show={showImportModal}
        handleClose={() => setShowImportModal(false)}
        refresh={() => getData()}
      />
      <FalconComponentCard>
        <FalconComponentCard.Header title="Contatos">
          <Flex>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="me-2"
              onClick={() => setShowImportModal(true)}
            >
              <FontAwesomeIcon
                icon={faArrowAltCircleDown}
                size="sm"
                className="me-2"
              />
              Importar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setShowModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
              Novo
            </Button>
          </Flex>
        </FalconComponentCard.Header>
        <FalconComponentCard.Body>
          <ContactCustomerEditModal
            showModal={showModalEdit}
            refreshData={() => getData()}
            data={selectedContact}
            handleClose={handleCloseModalEdit}
            handleSubmit={editContact}
            isSuccess={successEdit}
          />

          <ContactRegistrationModal
            showModal={showModal}
            handleClose={handleCloseModalRegister}
            newContact={response => {}}
            isSuccess={response => {
              if (response) getData();
            }}
          />

          <Row className="justify-content-between mb-3">
            <Col className="d-flex align-items-center col-auto pe-3 mb-3">
              <div className="position-relative input-search-width">
                <FormControl
                  size="sm"
                  id="search"
                  type="search"
                  placeholder="Buscar..."
                  value={filterName}
                  onChange={handleSearchChange}
                  className="rounded-pill ps-4"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-secondary"
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px'
                  }}
                />
              </div>
            </Col>

            <Col className="col-auto px-3">
              <Form.Select
                size="sm"
                className="mb-3 rounded-pill"
                onChange={e =>
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    includeInactives: e.target.value
                  }))
                }
              >
                <option value="true">Filtro por status</option>
                <option value="true">Todos</option>
                <option value="false">Somente ativos</option>
              </Form.Select>
            </Col>
          </Row>
          {isLoading ? (
            <Skeleton count={10} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <EmptyList imgSize={500} vhSize={73} />
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  table
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={contacts}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default ContactRegistration;
